import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import API from "@/lib/api";
import {
  LayoutDashboard, FolderOpen, Plus, Settings, ClipboardCheck, BarChart3,
  LogOut, User, TrendingUp, Bell, BookOpen, Trash2, Check, XCircle, Menu, X, ChevronDown, Download, Users, Mail
} from "lucide-react";
import { usePWAInstall } from "@/hooks/use-pwa-install";

export default function TopNav() {
  const location = useLocation();
  const { user, logout, isDirector, canCreateProjects, tier } = useAuth();
  const { branding } = useBranding();
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const drawerRef = useRef(null);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => { setMobileMenuOpen(false); setUserMenuOpen(false); setShowNotifs(false); }, [location.pathname]);

  // Click-outside handler for notifications and user menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifs && notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifs, userMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) { document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const fetchNotifs = useCallback(() => {
    API.get("/notifications/unread-count").then(r => setUnreadCount(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifs]);

  const openNotifs = async () => {
    if (!showNotifs) {
      try { const r = await API.get("/notifications"); setNotifications(r.data); } catch {}
    }
    setShowNotifs(!showNotifs);
    setUserMenuOpen(false);
  };

  const markRead = async (id) => {
    await API.put(`/notifications/${id}/read`).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await API.put("/notifications/read-all").catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (e, id) => {
    e.stopPropagation(); e.preventDefault();
    await API.delete(`/notifications/${id}`).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllRead = async () => {
    await API.delete("/notifications/read-all").catch(() => {});
    setNotifications(prev => prev.filter(n => !n.read));
  };

  const readCount = notifications.filter(n => n.read).length;

  const NAV_ITEMS = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/projects", label: "Projects", icon: FolderOpen },
    ...(canCreateProjects ? [{ to: "/projects/new", label: "New", icon: Plus }] : []),
    ...(tier >= 4 ? [{ to: "/approvals", label: "Approvals", icon: ClipboardCheck }] : []),
    ...(isDirector ? [{ to: "/benchmarking", label: "Benchmarking", icon: BarChart3 }] : []),
    ...(isDirector ? [{ to: "/intelligence", label: "Intelligence", icon: TrendingUp }] : []),
    ...(isDirector ? [{ to: "/contractors", label: "Contractors", icon: Users }] : []),
    { to: "/requests", label: "Requests", icon: Mail },
  ];

  const platformName = branding.platform_name || "MEP";
  const platformSuffix = branding.platform_suffix || ".Engine";

  const NavLink = ({ to, label, icon: Icon, mobile }) => {
    const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    if (mobile) {
      return (
        <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}-mobile`}
          onClick={() => setMobileMenuOpen(false)}
          className={`flex items-center gap-3 px-4 py-3.5 rounded-lg font-medium transition-all ${
            isActive ? "bg-blue-500/10 text-blue-400 border-l-2 border-blue-500" : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-200"
          }`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[15px]">{label}</span>
        </Link>
      );
    }
    return (
      <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
        className={`relative flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-md transition-all ${
          isActive ? "text-white bg-white/[0.08]" : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
        }`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        <span>{label}</span>
        {isActive && <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-blue-500 rounded-full" />}
      </Link>
    );
  };

  return (
    <>
      <header data-testid="top-nav" className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto flex items-center h-12 px-3 md:px-5">
          {/* Mobile hamburger */}
          <button data-testid="mobile-menu-btn" className="xl:hidden p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5 hover:opacity-90 transition-opacity flex-shrink-0 mr-6" data-testid="nav-logo">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={platformName} className="h-8 object-contain" />
            ) : (
              <img src="/furd-logo-dark.png" alt="FURD Tech" className="h-8 object-contain rounded" />
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-0.5 flex-1">
            {NAV_ITEMS.map(item => <NavLink key={item.to} {...item} mobile={false} />)}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1 ml-auto">
            <Link to="/manual" className="text-slate-500 hover:text-white transition-colors p-1.5 rounded-md hover:bg-white/[0.06] hidden sm:flex" title="User Manual" data-testid="nav-manual-btn">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            </Link>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button onClick={openNotifs} className="relative text-slate-500 hover:text-white transition-colors p-1.5 rounded-md hover:bg-white/[0.06]" data-testid="notification-bell" title="Notifications">
                <Bell className="w-4 h-4" strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                  <div className="absolute right-0 top-full mt-1.5 w-80 sm:w-96 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800">
                      <span className="text-sm font-semibold">Notifications</span>
                      <div className="flex items-center gap-3">
                        {readCount > 0 && <button onClick={clearAllRead} className="text-[11px] text-rose-400 hover:text-rose-300 font-medium flex items-center gap-1" data-testid="clear-all-read-btn"><XCircle className="w-3 h-3" />Clear read</button>}
                        {unreadCount > 0 && <button onClick={markAllRead} className="text-[11px] text-blue-400 hover:text-blue-300 font-medium" data-testid="mark-all-read-btn">Mark all read</button>}
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications</div>
                      ) : notifications.slice(0, 20).map(n => (
                        <div key={n.id} className={`group px-4 py-2.5 border-b border-slate-800/40 hover:bg-slate-800/40 transition-colors ${!n.read ? "bg-blue-500/5" : ""}`} data-testid={`notification-item-${n.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <Link to={n.link || `/projects/${n.project_id || ""}`} className="flex-1 min-w-0 block" onClick={() => { if (!n.read) markRead(n.id); setShowNotifs(false); }}>
                              <p className="text-sm font-medium text-slate-200 truncate">{n.title || n.project_name || "Notification"}</p>
                              <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message || (n.prev_status ? `${n.prev_status} → ${n.new_status}` : "")}</p>
                              <p className="text-[10px] text-slate-600 mt-0.5 font-mono">{new Date(n.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                            </Link>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!n.read && <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-1 text-slate-600 hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" title="Mark read" data-testid={`mark-read-${n.id}`}><Check className="w-3 h-3" /></button>}
                              {n.read && <button onClick={(e) => deleteNotification(e, n.id)} className="p-1 text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete" data-testid={`delete-notification-${n.id}`}><Trash2 className="w-3 h-3" /></button>}
                              {!n.read && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
              )}
            </div>

            {/* Separator */}
            <div className="w-px h-5 bg-white/[0.08] mx-1 hidden sm:block" />

            {/* User dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button onClick={() => { setUserMenuOpen(!userMenuOpen); setShowNotifs(false); }}
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/[0.06] transition-colors" data-testid="user-info">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-blue-400">{user?.name?.[0]?.toUpperCase()}</span>
                </div>
                <span className="text-xs font-medium text-slate-300 hidden md:inline max-w-[90px] truncate">{user?.name?.split(" ")[0]}</span>
                <ChevronDown className="w-3 h-3 text-slate-500 hidden md:inline" />
              </button>
              {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1.5 w-52 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="user-dropdown">
                    <div className="px-3 py-2.5 border-b border-slate-800">
                      <p className="text-sm font-medium text-slate-200">{user?.name}</p>
                      <p className="text-[11px] text-slate-500">{user?.email}</p>
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-semibold">{user?.tier_name || user?.role}</span>
                    </div>
                    <div className="py-1">
                      <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-testid="nav-profile-link">
                        <User className="w-3.5 h-3.5" />My Profile
                      </Link>
                      <Link to="/manual" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors sm:hidden">
                        <BookOpen className="w-3.5 h-3.5" />User Manual
                      </Link>
                      {isDirector && (
                        <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/[0.04] transition-colors" data-testid="nav-admin-link">
                          <Settings className="w-3.5 h-3.5" />Admin Settings
                        </Link>
                      )}
                      {canInstall && (
                        <button onClick={() => { promptInstall(); setUserMenuOpen(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
                          data-testid="install-app-btn">
                          <Download className="w-3.5 h-3.5" />Install Desktop App
                        </button>
                      )}
                      {isInstalled && (
                        <div className="flex items-center gap-2.5 px-3 py-2 text-sm text-emerald-500/70">
                          <Check className="w-3.5 h-3.5" />Installed as App
                        </div>
                      )}
                      <hr className="border-slate-800/50 my-1" />
                      <button onClick={() => { setUserMenuOpen(false); logout(); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-400 hover:text-rose-400 hover:bg-white/[0.04] transition-colors" data-testid="logout-btn">
                        <LogOut className="w-3.5 h-3.5" />Sign out
                      </button>
                    </div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div className={`mobile-nav-overlay ${mobileMenuOpen ? "open" : ""}`} onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-overlay" />
      <nav ref={drawerRef} className={`mobile-nav-drawer ${mobileMenuOpen ? "open" : ""}`} data-testid="mobile-nav-drawer">
        <div className="p-4 border-b border-slate-800/50 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={platformName} className="h-6 object-contain" />
            ) : (
              <img src="/furd-logo-dark.png" alt="FURD Tech" className="h-6 object-contain rounded" />
            )}
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-slate-400 hover:text-white rounded-md hover:bg-slate-800" data-testid="close-mobile-menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="py-3 px-3 flex flex-col gap-0.5">
          {NAV_ITEMS.map(item => <NavLink key={item.to} {...item} mobile={true} />)}
          <hr className="border-slate-800/50 my-2" />
          <Link to="/manual" onClick={() => setMobileMenuOpen(false)} data-testid="nav-manual-mobile"
            className="flex items-center gap-3 px-4 py-3.5 rounded-lg font-medium text-slate-400 hover:bg-slate-800/80 hover:text-slate-200 transition-all">
            <BookOpen className="w-5 h-5" strokeWidth={1.5} /><span className="text-[15px]">User Manual</span>
          </Link>
          <Link to="/profile" onClick={() => setMobileMenuOpen(false)} data-testid="nav-profile-mobile"
            className="flex items-center gap-3 px-4 py-3.5 rounded-lg font-medium text-slate-400 hover:bg-slate-800/80 hover:text-slate-200 transition-all">
            <User className="w-5 h-5" strokeWidth={1.5} /><span className="text-[15px]">My Profile</span>
          </Link>
          {isDirector && (
            <Link to="/admin" onClick={() => setMobileMenuOpen(false)} data-testid="nav-admin-mobile"
              className="flex items-center gap-3 px-4 py-3.5 rounded-lg font-medium text-slate-400 hover:bg-slate-800/80 hover:text-slate-200 transition-all">
              <Settings className="w-5 h-5" strokeWidth={1.5} /><span className="text-[15px]">Admin Settings</span>
            </Link>
          )}
        </div>
        <div className="mt-auto p-4 border-t border-slate-800/50">
          {canInstall && (
            <button onClick={() => { promptInstall(); setMobileMenuOpen(false); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2 rounded-lg bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 text-blue-400 transition-colors text-sm font-medium"
              data-testid="install-app-btn-mobile">
              <Download className="w-4 h-4" />Install Desktop App
            </button>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-sm font-bold text-blue-400">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.role}</p>
            </div>
          </div>
          <button onClick={() => { setMobileMenuOpen(false); logout(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors text-sm font-medium"
            data-testid="logout-btn-mobile">
            <LogOut className="w-4 h-4" />Sign Out
          </button>
        </div>
      </nav>
    </>
  );
}
